/**
 * Glowvelle PDP frequently-bought-together — multi-add for AOV.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

(() => {
  const root = document.querySelector('[data-gv-fbt]');
  if (!root) return;

  const checks = [...root.querySelectorAll('[data-gv-fbt-check]')];
  const totalEl = root.querySelector('[data-gv-fbt-total]');
  const labelEl = root.querySelector('[data-gv-fbt-label]');
  const addBtn = root.querySelector('[data-gv-fbt-add]');
  const shipEl = root.querySelector('[data-gv-fbt-ship]');
  const basePrice = Number(root.dataset.basePrice || 0);
  const baseVariant = root.dataset.baseVariant || '';
  const threshold = Number(shipEl?.dataset.threshold || 6000);
  let adding = false;

  const moneyFmt = (cents) => {
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  function selectedExtras() {
    return checks
      .filter((c) => c.checked)
      .map((c) => ({
        id: c.getAttribute('data-variant-id'),
        price: Number(c.getAttribute('data-price') || 0),
      }))
      .filter((x) => x.id);
  }

  function syncTotal() {
    const extras = selectedExtras();
    const sum = basePrice + extras.reduce((a, x) => a + x.price, 0);
    if (totalEl) totalEl.textContent = moneyFmt(sum);
    if (labelEl) {
      labelEl.textContent = extras.length === 0 ? 'Add this item · ' : 'Add selected · ';
    }
    if (addBtn) addBtn.disabled = !baseVariant && extras.length === 0;
    if (shipEl) {
      const need = Math.max(0, threshold - sum);
      if (need <= 0) {
        shipEl.innerHTML = '<strong>You’ve unlocked free shipping</strong>';
      } else {
        shipEl.innerHTML = `Add <strong data-gv-fbt-ship-amt>${moneyFmt(need)}</strong> more for free shipping`;
      }
    }
  }

  async function refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent?.fetchCartData) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }
    const res = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
    return res.json();
  }

  function openCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    if (window.Shopify?.actions?.openCart) {
      window.Shopify.actions.openCart();
    }
  }

  async function addSelected() {
    if (adding) return;
    const extras = selectedExtras();
    const items = [];
    if (baseVariant) items.push({ id: Number(baseVariant), quantity: 1 });
    extras.forEach((x) => items.push({ id: Number(x.id), quantity: 1 }));
    if (!items.length) return;

    adding = true;
    addBtn.disabled = true;
    const prev = addBtn.innerHTML;
    addBtn.textContent = 'Adding…';

    const formData = { items };

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.sections = [...new Set(sectionIds)].join(',');
      formData.sections_url = window.location.pathname;
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    addBtn.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((x) => ({ merchandiseId: String(x.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const fetchCfg = fetchConfig('javascript');
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      }).then((res) => res.json());

      if (response.status) {
        addBtn.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
          })
        );
        const ajaxCart = await refreshCart().catch(() => null);
        deferred.resolve({
          cart: ajaxCart
            ? CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart)
            : { totalQuantity: 0, cost: {}, lines: [] },
          detail: { didError: true, items: ajaxCart?.items || [], source: 'glowvelle-pdp-fbt', itemCount: items.length },
        });
        return;
      }

      const ajaxCart = await refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'glowvelle-pdp-fbt',
          itemCount: items.length,
          sections: response.sections,
          didError: false,
        },
      });

      document.querySelectorAll('[data-gv-cart-count]').forEach((el) => {
        el.textContent = String(ajaxCart.item_count);
      });
      openCartDrawer();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      addBtn.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      adding = false;
      addBtn.disabled = false;
      addBtn.innerHTML = prev;
      syncTotal();
    }
  }

  checks.forEach((c) => c.addEventListener('change', syncTotal));
  addBtn?.addEventListener('click', addSelected);
  syncTotal();
})();
